import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked, AfterViewInit } from '@angular/core';
import { ApexOptions, ChartComponent } from 'ng-apexcharts';
import { LgvAverageRatesService } from '../../core/services/lgv-average-rates.service';
import { ContainerHeightService } from '../../core/services/container-height.service';

export type ChartOptions = ApexOptions;

@Component({
    selector: 'lgv-mortgage-rate-history',
    templateUrl: './mortgage-rate-history.component.html',
    styleUrls: ['./mortgage-rate-history.component.scss'],
})
export class MortgageRateHistoryComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
    @ViewChild('chart', { static: false }) chart: ChartComponent;

    public chartOptions: Partial<ChartOptions>;

    private minDate: number;
    public maxDate: number;
    public seriesVisible: boolean[] = [];
    public seriesColors: string[] = ['#4A2F89', '#E77A5D', '#51B1AE', '#fcba4a'];
    public currentZoom: string = '6m';

    constructor(
        public lgvAvgRatesSvc: LgvAverageRatesService,
        private elementRef: ElementRef,
        private containerHeightService: ContainerHeightService
    ) { }

    @ViewChild('outerContainer') outerContainerRef: ElementRef;

    ngOnInit(): void {
        // Fetch the daily rates
        this.lgvAvgRatesSvc.fetchDailyRates();

        // Subscribe to the dailyRates$ observable
        this.lgvAvgRatesSvc.dailyRates$.subscribe((data) => {
            if (data) {
                this.processData(data);
            }
        });
    }

    ngAfterViewInit(): void {
        this.containerHeightService.startIframeHeightReporting(
            'mortgage-rate-history',
            this.outerContainerRef
        );
    }

    ngAfterViewChecked(): void {
        const element = this.elementRef.nativeElement;
        if(element.offsetParent === null) {
            // console.log('***HIDDEN***');
        }else{
            // console.log('***VISIBLE***');
            // refresh from being hidden
            this.setZoom(this.currentZoom);
        }
    }

    ngOnDestroy(): void {
        this.containerHeightService.stopHeightReporting('mortgage-rate-history');
    }

    processData(rawData: any[]): void {
        const products = [
            '30 YEAR CONFORMING',
            '15 YEAR CONFORMING',
            '30 YEAR FHA',
            '30 YEAR JUMBO',
        ];

        // Find the min and max dates in the dataset
        const timestamps = rawData.map((entry) =>
            new Date(entry.timestamp).getTime()
        );
        this.minDate = Math.min(...timestamps);
        this.maxDate = Math.max(...timestamps);

        // Set initial value
        const oneYearInMs = 365 * 24 * 60 * 60 * 1000;
        const defaultMinDate = this.maxDate - oneYearInMs;
        const initialMinDate = Math.max(defaultMinDate, this.minDate);

        const seriesData = products.map((product, index) => {
            const productData = rawData
                .filter((entry) => entry.product === product)
                .sort(
                    (a, b) =>
                        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
                );

            return {
                name: product,
                data: productData.map((entry) => ({
                    x: new Date(entry.timestamp),
                    y: entry.averageRateToday,
                })),
            };
        });

        // Initialize series visibility
        this.seriesVisible = new Array(seriesData.length).fill(true);

        this.chartOptions = {
            series: seriesData,
            colors: this.seriesColors,
            legend: {
                show: false,
            },
            chart: {
                type: 'line',
                id: 'mainChart',
                fontFamily: 'Roboto, "Helvetica Neue", sans-serif',
                height: 350,
                toolbar: {
                    autoSelected: 'pan',
                    show: false
                }
            },
            xaxis: {
                type: 'datetime',
                labels: {
                    datetimeUTC: false,
                    format: 'MMM dd',
                    style: {
                        fontSize: '14px'
                    }
                },
            },
            yaxis: {
                labels: {
                    formatter: function (value) {
                        return value.toFixed(3) + '%';
                    },
                    style: {
                        fontSize: '14px'
                    }
                },
            },
            tooltip: {
                x: {
                    format: 'MM/dd/yyyy',
                },
            },
            stroke: {
                curve: 'smooth',
                width: 3,
            },
            markers: {
                size: 0,
            },
            dataLabels: {
                enabled: false,
                style: {
                    fontSize: '18px',
                    fontWeight: '400',
                    colors: undefined
                },
            },
            grid: {
                row: {
                    colors: ["#f3f3f3", "transparent"],
                    opacity: 0.5
                }
            },
        };
        
        // Trigger height check after chart data is processed
        setTimeout(() => {
            this.containerHeightService.triggerHeightCheck('mortgage-rate-history');
        }, 100);
    }

    setZoom(range: string): void {
        let zoomMinDate: number;
        const maxDate = this.maxDate;

        switch (range) {
            case '1w':
                zoomMinDate = maxDate - 7 * 24 * 60 * 60 * 1000; // 7 days ago
                break;
            case '1m':
                zoomMinDate = maxDate - 30 * 24 * 60 * 60 * 1000; // 30 days ago
                break;
            case '3m':
                zoomMinDate = maxDate - 90 * 24 * 60 * 60 * 1000; // 90 days ago
                break;
            case '6m':
                zoomMinDate = maxDate - 180 * 24 * 60 * 60 * 1000; // 180 days ago
                break;
            case '1y':
                zoomMinDate = maxDate - 365 * 24 * 60 * 60 * 1000; // 365 days ago
                break;
            case 'all':
            default:
                zoomMinDate = this.minDate;
                break;
        }

        // Ensure zoomMinDate doesn't go below the earliest date
        zoomMinDate = Math.max(this.minDate, zoomMinDate);

        // Update the chart's xaxis range
        this.chart.updateOptions({
            xaxis: {
                min: zoomMinDate,
                max: maxDate,
            },
        });

        this.currentZoom = range;
        
        // Trigger height check after chart updates
        this.containerHeightService.triggerHeightCheck('mortgage-rate-history');
    }

    toggleSeries(seriesIndex: number): void {
        this.seriesVisible[seriesIndex] = !this.seriesVisible[seriesIndex];

        const updatedSeries = this.chartOptions.series.map((s, i) => ({
            ...s,
            data: this.seriesVisible[i] ? s.data : [],
        }));

        this.chart.updateSeries(updatedSeries);
        
        // Trigger height check after series toggle
        this.containerHeightService.triggerHeightCheck('mortgage-rate-history');
    }

    // reportContainerHeight() {
    //     setTimeout(() => {
    //         let height = (<HTMLElement>this.outerContainerRef.nativeElement).getBoundingClientRect().height;
    //         window.parent.postMessage({ currentHeight: height }, '*');
    //     }, 100);
    // }
}
